import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { Chip } from '@jotpsych/ui';
import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Chip as MuiChip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { MAX_LABEL_LENGTH, requirementKindLabel } from '../constants';
import type { DefinitionResponse, SubjectLanguage } from '../types';
import { materializationReport } from '../vocabulary';

import { useCommittedTextField } from './useCommittedTextField';

/** Columns in the catalog table; the detail row spans all of them. */
export const CATALOG_COLUMN_COUNT = 7;

/**
 * Columns a phone drops. With all seven, the table needs a horizontal scroll,
 * and the expanded detail inside it ran off the right edge. What these show is
 * repeated in the detail (the name, the assignment switch, the last send), so
 * a phone loses nothing by hiding them.
 */
export const WIDE_ONLY_CELL = { display: { xs: 'none', md: 'table-cell' } } as const;

export interface DefinitionPatch {
  label?: string;
  patient_instructions?: string;
  active?: boolean;
  default_for_new_patients?: boolean;
}

export interface RequirementDefinitionRowProps {
  definition: DefinitionResponse;
  subject: SubjectLanguage;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** Rejects to let the row surface the failure. */
  onUpdate: (patch: DefinitionPatch) => Promise<void>;
  /**
   * Connected pieces the container supplies (GDN-4142). Nodes rather than
   * flags, so this component knows nothing about queries or permissions, and
   * its tests and stories render it with no store.
   */
  missingCell?: ReactNode;
  lastSendCell?: ReactNode;
  /** The practice-wide text and email, under "What patients see". */
  messagePreview?: ReactNode;
  /** The count and the send button, for an active requirement. */
  sendRequests?: ReactNode;
  /**
   * The newest bulk send, shown whether or not a send is on offer: a phone
   * hides the Last bulk send column, so this is its only place there.
   */
  lastSendDetail?: ReactNode;
  disabled?: boolean;
}

const SectionLabel = ({ children, id }: { children: string; id: string }) => (
  <Typography
    id={id}
    component="h3"
    sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: 1.25,
    }}
  >
    {children}
  </Typography>
);

const StepLabel = ({ children }: { children: string }) => (
  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.75 }}>
    {children}
  </Typography>
);

const kindIcon = (kind: string) =>
  kind === 'payment_method' ? (
    <CreditCardOutlined fontSize="small" />
  ) : (
    <BadgeOutlined fontSize="small" />
  );

/**
 * One requirement in the catalog table (GDN-4142, the approved catalog-table
 * design from GDN-4123).
 *
 * At rest a row is a read: status, auto-assign, how many are missing it and
 * the last bulk send, with nothing to operate but the row itself. Expanded, it
 * holds everything edited per requirement — what patients see, the two
 * switches, and the way into Send requests, which opens as a modal.
 *
 * Status is a switch in the detail, not a clickable chip in the row: turning a
 * requirement off stops new assignment for the whole practice, and a chip that
 * does that on a stray click is too easy to hit while scanning the table.
 *
 * The name and the instructions write only when the admin commits them (blur,
 * or Enter on the name), never while typing — see `useCommittedTextField`.
 */
export const RequirementDefinitionRow = ({
  definition,
  subject,
  expanded,
  onToggleExpanded,
  onUpdate,
  missingCell,
  lastSendCell,
  messagePreview,
  sendRequests,
  lastSendDetail,
  disabled = false,
}: RequirementDefinitionRowProps) => {
  const kindName = requirementKindLabel(definition.kind);
  const isChecking = definition.materialization_status === 'checking';
  const detailId = `requirement-detail-${definition.patient_requirement_definition_id}`;

  const saveLabel = useCallback((label: string) => onUpdate({ label }), [onUpdate]);
  const saveInstructions = useCallback(
    // An empty string, not null: the PATCH reads null as "leave unchanged", so
    // clearing the instructions has to send the empty value itself.
    (patient_instructions: string) => onUpdate({ patient_instructions }),
    [onUpdate],
  );
  const label = useCommittedTextField({ value: definition.label, onSave: saveLabel });
  const instructions = useCommittedTextField({
    value: definition.patient_instructions ?? '',
    onSave: saveInstructions,
    allowEmpty: true,
    multiline: true,
  });

  const unsaved = !expanded && (label.failed || instructions.failed);

  /**
   * The finished counts, shown once, to the person who watched it run
   * (GDN-3589).
   *
   * Session-scoped by construction: this only becomes true when THIS row
   * observes the status change from checking to done, so a reload, a different
   * tab, or arriving after the run finished all show nothing. The missing count
   * is the live source of these numbers; a "just finished" banner that
   * outlived the moment would be a second, staler one.
   */
  const [justFinished, setJustFinished] = useState(false);
  const wasCheckingRef = useRef(isChecking);
  useEffect(() => {
    if (wasCheckingRef.current && !isChecking) setJustFinished(true);
    wasCheckingRef.current = isChecking;
  }, [isChecking]);

  return (
    <>
      <TableRow
        hover
        selected={expanded}
        onClick={onToggleExpanded}
        sx={{
          cursor: 'pointer',
          '& > td': { borderBottom: expanded || justFinished ? 0 : undefined },
          bgcolor: definition.active || expanded ? undefined : 'grey.50',
        }}
      >
        <TableCell>
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'flex-start' }}>
            <Box sx={{ color: 'text.secondary', display: 'flex', mt: 0.25 }}>
              {kindIcon(definition.kind)}
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {kindName}
              </Typography>
              {/*
                The Name column is hidden on a phone, and two requirements can
                share a kind, so the name rides here where a phone shows it.
              */}
              <Typography
                variant="body2"
                sx={{ display: { xs: 'block', md: 'none' }, overflowWrap: 'anywhere' }}
              >
                {label.value}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {definition.kind === 'payment_method'
                  ? `Built in. Satisfied when the ${subject.singular} has a saved card.`
                  : `Satisfied when the ${subject.singular} provides this item.`}
              </Typography>
              {/*
                The run is visible on the row it belongs to (GDN-3589). Running
                or finished, never a fraction: the backend keeps no running
                total, so "checked 412 of 1,200" would be invented here.
              */}
              {/*
                A refused save must never be invisible (GDN-3525). The field's
                own indicator lives in the detail, so once the row is closed
                this line is the only sign the name on screen is not saved.
              */}
              {unsaved && (
                <Typography
                  variant="caption"
                  role="status"
                  sx={{ display: 'block', color: 'error.main', mt: 0.5 }}
                >
                  A change was not saved. Open to try again.
                </Typography>
              )}
              {isChecking && (
                <MuiChip
                  size="small"
                  variant="outlined"
                  role="status"
                  aria-live="polite"
                  icon={<CircularProgress size={11} sx={{ ml: 0.75 }} />}
                  label="Checking existing records…"
                  sx={{ mt: 0.75, height: 22, fontSize: 11.5 }}
                />
              )}
            </Box>
          </Stack>
        </TableCell>
        <TableCell sx={WIDE_ONLY_CELL}>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {label.value}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={definition.active ? 'Active' : 'Off'}
            color={definition.active ? 'success' : 'default'}
            size="xSmall"
            variant="soft"
          />
        </TableCell>
        <TableCell sx={WIDE_ONLY_CELL}>
          {definition.default_for_new_patients ? (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <AutorenewRounded fontSize="inherit" color="primary" />
              <Typography variant="body2">{`New ${subject.plural}`}</Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Manual
            </Typography>
          )}
        </TableCell>
        <TableCell>{missingCell}</TableCell>
        <TableCell sx={WIDE_ONLY_CELL}>{lastSendCell}</TableCell>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            aria-expanded={expanded}
            aria-controls={detailId}
            // The label, not only the kind: two requirements can share a kind,
            // and a screen reader moving through the table must tell them apart.
            aria-label={`${expanded ? 'Hide' : 'Show'} details for ${label.value} (${kindName})`}
            onClick={event => {
              // The row toggles on click too; one toggle, not two.
              event.stopPropagation();
              onToggleExpanded();
            }}
          >
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>

      {/*
        The loud report, once (GDN-3589). Its own row, outside the collapsible
        detail, so the person who watched the run sees it whether or not the
        row is open. Dismissible, and gone on reload — see `justFinished`.
      */}
      {justFinished && (
        <TableRow>
          <TableCell
            colSpan={CATALOG_COLUMN_COUNT}
            sx={{ pt: 0, borderBottom: expanded ? 0 : undefined }}
          >
            <Alert
              severity="success"
              variant="outlined"
              role="status"
              onClose={() => setJustFinished(false)}
              sx={{ py: 0.25, fontSize: 12.5, alignItems: 'center' }}
            >
              {materializationReport(definition, subject)}
            </Alert>
          </TableCell>
        </TableRow>
      )}

      <TableRow id={detailId}>
        <TableCell
          colSpan={CATALOG_COLUMN_COUNT}
          sx={{
            py: 0,
            bgcolor: expanded ? 'grey.50' : undefined,
            borderBottom: expanded ? undefined : 0,
          }}
        >
          <Collapse in={expanded} unmountOnExit>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '1.6fr 1fr' },
                gap: 4,
                py: 2.5,
                px: 1,
              }}
            >
              <Box component="section" aria-labelledby={`${detailId}-sees`}>
                <SectionLabel id={`${detailId}-sees`}>{`What ${subject.plural} see`}</SectionLabel>
                <Stack spacing={2}>
                  <Box>
                    <StepLabel>{`1. The text and email ${subject.plural} get`}</StepLabel>
                    {messagePreview}
                  </Box>
                  <Box>
                    <StepLabel>2. The page the link opens</StepLabel>
                    <Stack spacing={1.5}>
                      <Box>
                        <TextField
                          size="small"
                          fullWidth
                          label="Name"
                          disabled={disabled}
                          {...label.inputProps}
                          slotProps={{ htmlInput: { maxLength: MAX_LABEL_LENGTH } }}
                          sx={{ bgcolor: 'background.paper' }}
                        />
                        {label.showStatus && (
                          <AutosaveStatus
                            status={label.status}
                            isDirty={label.isDirty}
                            lastSavedAt={label.lastSavedAt}
                          />
                        )}
                      </Box>
                      <Box>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          label="Instructions"
                          placeholder="Optional. Tell them what to do or have ready."
                          disabled={disabled}
                          {...instructions.inputProps}
                          sx={{ bgcolor: 'background.paper' }}
                        />
                        {instructions.showStatus && (
                          <AutosaveStatus
                            status={instructions.status}
                            isDirty={instructions.isDirty}
                            lastSavedAt={instructions.lastSavedAt}
                          />
                        )}
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              <Stack spacing={3}>
                <Box component="section" aria-labelledby={`${detailId}-assignment`}>
                  <SectionLabel id={`${detailId}-assignment`}>Status and assignment</SectionLabel>
                  <Stack spacing={0.5}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={definition.active}
                          disabled={disabled}
                          onChange={event => {
                            void onUpdate({ active: event.target.checked });
                          }}
                        />
                      }
                      label={<Typography variant="body2">Active</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={definition.default_for_new_patients}
                          disabled={disabled}
                          onChange={event => {
                            void onUpdate({ default_for_new_patients: event.target.checked });
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">{`Assign to new ${subject.plural} automatically`}</Typography>
                      }
                    />
                    <Typography variant="caption" color="text.secondary">
                      Assigning does not send a message.
                    </Typography>
                  </Stack>
                </Box>

                <Box component="section" aria-labelledby={`${detailId}-send`}>
                  <SectionLabel id={`${detailId}-send`}>Send requests</SectionLabel>
                  <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                    {definition.active ? (
                      sendRequests
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Turn this requirement on to send requests.
                      </Typography>
                    )}
                    {lastSendDetail}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
